/**
 * Bedrijfsgegevens - Instellingen
 */

import { useState, useEffect } from 'react';
import { bedrijfsgegevensService, BedrijfsgegevensUpdate } from '../../services/bedrijfsgegevens.service';
import Toast from '../../components/Toast';
import './Settings.css';
import '../relations/Relations.css';

const Bedrijfsgegevens = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState<BedrijfsgegevensUpdate>({
    bedrijfsnaam: '',
    bedrijfsnaam_regel1: '',
    bedrijfsnaam_regel2: '',
    eigenaar_voornaam: '',
    eigenaar_achternaam: '',
    straat: '',
    huisnummer: '',
    postcode: '',
    plaats: '',
    land: 'Nederland',
    telefoon: '',
    mobiel: '',
    email: '',
    website: '',
    kvk_nummer: '',
    btw_nummer: '',
    iban: '',
    banknaam: '',
    logo_pad: '',
    factuur_footer: '',
    algemene_voorwaarden: '',
    
  });

  useEffect(() => {
    loadBedrijfsgegevens();
  }, []);

  const loadBedrijfsgegevens = async () => {
    try {
      setLoading(true);
      const data = await bedrijfsgegevensService.get();
      setFormData({
        bedrijfsnaam: data.bedrijfsnaam,
        bedrijfsnaam_regel1: data.bedrijfsnaam_regel1 || '',
        bedrijfsnaam_regel2: data.bedrijfsnaam_regel2 || '',
        eigenaar_voornaam: data.eigenaar_voornaam || '',
        eigenaar_achternaam: data.eigenaar_achternaam || '',
        straat: data.straat || '',
        huisnummer: data.huisnummer || '',
        postcode: data.postcode || '',
        plaats: data.plaats || '',
        land: data.land || 'Nederland',
        telefoon: data.telefoon || '',
        mobiel: data.mobiel || '',
        email: data.email || '',
        website: data.website || '',
        kvk_nummer: data.kvk_nummer || '',
        btw_nummer: data.btw_nummer || '',
        iban: data.iban || '',
        banknaam: data.banknaam || '',
        logo_pad: data.logo_pad || '',
        factuur_footer: data.factuur_footer || '',
        algemene_voorwaarden: data.algemene_voorwaarden || '',
        
      });
    } catch (err: any) {
      setError(err.message || 'Fout bij ophalen bedrijfsgegevens');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'number' ? parseInt(value) || 0 : 
              type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              value 
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.bedrijfsnaam_regel1) {
      setError('Bedrijfsnaam Regel 1 is verplicht');
      return;
    }

    try {
      setLoading(true);
      // Auto-generate bedrijfsnaam from regel1 + regel2
      const dataToSave = {
        ...formData,
        bedrijfsnaam: `${formData.bedrijfsnaam_regel1 || ''} ${formData.bedrijfsnaam_regel2 || ''}`.trim()
      };
      await bedrijfsgegevensService.update(dataToSave);
      setSuccess('Bedrijfsgegevens succesvol opgeslagen!');
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het opslaan');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.bedrijfsnaam_regel1) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Bedrijfsgegevens</h1>
      </div>

      <Toast message={success} type="success" onClose={() => setSuccess(null)} />
      <Toast message={error} type="error" onClose={() => setError(null)} autoClose={0} />

      <form onSubmit={handleSubmit} className="form-container">
        {/* Basis Informatie */}
        <section className="form-section">
          <h2>Basis Informatie</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="bedrijfsnaam_regel1">
                Bedrijfsnaam <span className="required">*</span>
              </label>
              <input
                type="text"
                id="bedrijfsnaam_regel1"
                name="bedrijfsnaam_regel1"
                value={formData.bedrijfsnaam_regel1}
                onChange={handleChange}
                required
                placeholder="Kramer"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bedrijfsnaam_regel2">&nbsp;</label>
              <input
                type="text"
                id="bedrijfsnaam_regel2"
                name="bedrijfsnaam_regel2"
                value={formData.bedrijfsnaam_regel2}
                onChange={handleChange}
                placeholder="Nautical Services"
              />
            </div>

            <div className="form-group">
              <label htmlFor="eigenaar_voornaam">Eigenaar Voornaam</label>
              <input
                type="text"
                id="eigenaar_voornaam"
                name="eigenaar_voornaam"
                value={formData.eigenaar_voornaam}
                onChange={handleChange}
                placeholder="Voornaam"
              />
            </div>

            <div className="form-group">
              <label htmlFor="eigenaar_achternaam">Eigenaar Achternaam</label>
              <input
                type="text"
                id="eigenaar_achternaam"
                name="eigenaar_achternaam"
                value={formData.eigenaar_achternaam}
                onChange={handleChange}
                placeholder="Achternaam"
              />
            </div>
          </div>
        </section>

        {/* Adres Gegevens */}
        <section className="form-section">
          <h2>Adres Gegevens</h2>
          <div className="form-grid">
            <div className="form-group span-2">
              <label htmlFor="straat">Straat</label>
              <input
                type="text"
                id="straat"
                name="straat"
                value={formData.straat}
                onChange={handleChange}
                placeholder="Straatnaam"
              />
            </div>

            <div className="form-group">
              <label htmlFor="huisnummer">Huisnummer</label>
              <input
                type="text"
                id="huisnummer"
                name="huisnummer"
                value={formData.huisnummer}
                onChange={handleChange}
                placeholder="123"
              />
            </div>

            <div className="form-group">
              <label htmlFor="postcode">Postcode</label>
              <input
                type="text"
                id="postcode"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
                placeholder="1234 AB"
              />
            </div>

            <div className="form-group">
              <label htmlFor="plaats">Plaats</label>
              <input
                type="text"
                id="plaats"
                name="plaats"
                value={formData.plaats}
                onChange={handleChange}
                placeholder="Plaatsnaam"
              />
            </div>

            <div className="form-group">
              <label htmlFor="land">Land</label>
              <input
                type="text"
                id="land"
                name="land"
                value={formData.land}
                onChange={handleChange}
                placeholder="Nederland"
              />
            </div>
          </div>
        </section>

        {/* Contact Gegevens */}
        <section className="form-section">
          <h2>Contact Gegevens</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="telefoon">Telefoon</label>
              <input
                type="tel"
                id="telefoon"
                name="telefoon"
                value={formData.telefoon}
                onChange={handleChange}
                placeholder="+31 20 1234567"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mobiel">Mobiel</label>
              <input
                type="tel"
                id="mobiel"
                name="mobiel"
                value={formData.mobiel}
                onChange={handleChange}
                placeholder="+31 6 12345678"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="info@bedrijf.nl"
              />
            </div>

            {/* type="url" blokkeerde opslaan: de website staat bewust als
                "www.Buro42.nl" in de huisstijl, zonder https ervoor, en de
                browser weigert dat als URL. Gevonden 25-10-2026. */}
            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="www.bedrijf.nl"
              />
            </div>
          </div>
        </section>

        {/* Bedrijfsgegevens */}
        <section className="form-section">
          <h2>Bedrijfsgegevens</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="kvk_nummer">KvK-nummer</label>
              <input
                type="text"
                id="kvk_nummer"
                name="kvk_nummer"
                value={formData.kvk_nummer}
                onChange={handleChange}
                placeholder="12345678"
              />
            </div>

            <div className="form-group">
              <label htmlFor="btw_nummer">BTW-nummer</label>
              <input
                type="text"
                id="btw_nummer"
                name="btw_nummer"
                value={formData.btw_nummer}
                onChange={handleChange}
                placeholder="NL123456789B01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="iban">IBAN</label>
              <input
                type="text"
                id="iban"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                placeholder="NL12ABCD0123456789"
              />
            </div>

            <div className="form-group">
              <label htmlFor="banknaam">Naam bank</label>
              <input
                type="text"
                id="banknaam"
                name="banknaam"
                value={formData.banknaam}
                onChange={handleChange}
                placeholder="bijv. KNAB"
              />
            </div>
          </div>
        </section>

        {/* Document Instellingen */}
        <section className="form-section">
          <h2>Document Instellingen</h2>
          <div className="form-group">
            <label htmlFor="factuur_footer">Factuur Footer</label>
            <textarea
              id="factuur_footer"
              name="factuur_footer"
              value={formData.factuur_footer}
              onChange={handleChange}
              rows={4}
              placeholder="Tekst die onderaan facturen wordt getoond..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="algemene_voorwaarden">Algemene Voorwaarden</label>
            <textarea
              id="algemene_voorwaarden"
              name="algemene_voorwaarden"
              value={formData.algemene_voorwaarden}
              onChange={handleChange}
              rows={6}
              placeholder="Uw algemene voorwaarden..."
            />
          </div>
        </section>


        <div className="form-actions">
          <button
            type="submit"
            className="btn-lg btn-success"
            disabled={loading}
          >
            {loading ? 'Bezig met opslaan...' : 'Opslaan'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Bedrijfsgegevens;